import { MapPin, Unlink } from 'lucide-react'
import { originLegend, originText, type CoordinateOriginMix } from './coordinateOrigin'
import { formatFigure } from './FigureValue'
import { useDrillDown } from './useDrillDown'
import { usGeography } from './usGeography'
import type { DrillDescriptor } from '@/types/dashboards'

export interface CityConcentrationPoint extends CoordinateOriginMix {
  city: string
  state: string
  latitude: number
  longitude: number
  coordinateBasis: string
  dealCount: number
  loanAmount: number
  shareOfMappedPct: number | null
  shareOfBookPct: number | null
  sourceLocationCount: number
  drill: DrillDescriptor | null
}

export interface CityConcentrationCoverage {
  totalDeals: number
  totalAmount: number
  mappedDeals: number
  mappedAmount: number
  mappedAmountPctOfBook: number | null
  unmappedDeals: number
  unmappedAmount: number
  excludedNonContinentalDeals: number
  excludedNonContinentalAmount: number
  unmappedReasons: Array<{ code: string; dealCount: number; loanAmount: number; dealIds?: string[] }>
  /** The deals not on the map, when the server names them (T-50). */
  unmappedIds?: string[]
}

export interface CityConcentrationData extends CoordinateOriginMix {
  state: 'empty' | 'unmapped' | 'partial' | 'complete'
  scope: string
  coordinateSource: string
  coordinateAggregation: string
  /** How the mapped coordinates were resolved, keyed by precision:
   *  rooftop / street / city / unrecorded. A city-level point places the deal
   *  in the right municipality and says nothing about where in it the asset
   *  sits, so the footnote reports the mix rather than implying all points
   *  locate a building. Optional — a board built before the geocode backfill
   *  landed ships neither field. */
  coordinatePrecision?: Record<string, number>
  assetLevelMappedDeals?: number
  cityLevelMappedDeals?: number
  points: CityConcentrationPoint[]
  coverage: CityConcentrationCoverage
  /** The deals not on the map, by name and reason (T-50). Optional — a board
   *  built before 2 Nov ships neither. */
  unmappedDeals?: Array<{
    id: string
    name: string | null
    reason: string
    reasonLabel?: string | null
    fixable?: boolean
    amount?: number | null
  }>
  unmappedDrill?: DrillDescriptor | null
  unmappedReasonLabels?: Record<string, string>
}

const VIEW_WIDTH = 760
const VIEW_HEIGHT = 330
const PLOT = { left: 34, right: 18, top: 18, bottom: 36 }
const LNG = { min: -125, max: -66 }
const LAT = { min: 24, max: 50 }

/** The one projection. Markers and geography both go through it, so the
 *  coastline cannot drift away from the dots. */
function projectLngLat(longitude: number, latitude: number): { x: number; y: number } {
  const width = VIEW_WIDTH - PLOT.left - PLOT.right
  const height = VIEW_HEIGHT - PLOT.top - PLOT.bottom
  return {
    x: PLOT.left + ((longitude - LNG.min) / (LNG.max - LNG.min)) * width,
    y: PLOT.top + ((LAT.max - latitude) / (LAT.max - LAT.min)) * height,
  }
}

/** Built once for this fixed viewport — the plot never resizes its viewBox. */
const GEOGRAPHY = usGeography(
  projectLngLat,
  `${VIEW_WIDTH}x${VIEW_HEIGHT}:${LNG.min},${LNG.max},${LAT.min},${LAT.max}`,
)

function project(point: CityConcentrationPoint): { x: number; y: number } {
  const width = VIEW_WIDTH - PLOT.left - PLOT.right
  const height = VIEW_HEIGHT - PLOT.top - PLOT.bottom
  return {
    x: PLOT.left + ((point.longitude - LNG.min) / (LNG.max - LNG.min)) * width,
    y: PLOT.top + ((LAT.max - point.latitude) / (LAT.max - LAT.min)) * height,
  }
}

function money(value: number): string {
  return formatFigure({ label: '', value, format: 'currencyM' })
}

function percent(value: number | null): string {
  return formatFigure({ label: '', value, format: 'pct' })
}

function reasonLabel(value: string): string {
  return value.replace(/_/g, ' ')
}

/**
 * One proportional-symbol geographic plot. Every marker coordinate came from
 * the server's linked Property row — already stored on it, or geocoded from its
 * address by the sourcing sweep and labelled as not reviewed; this component
 * never geocodes or substitutes a city/default coordinate. The ranked list is
 * the non-hover/mobile reading path and carries the same drill descriptor as
 * the marker's city.
 */
export default function CityConcentrationMap({
  data,
  unmappedDrill,
}: {
  data: CityConcentrationData
  /** Opens the deals that are not on the map (T-50). The server's own id list
   *  wins; a caller may supply one built from the same cohort's deal facts. */
  unmappedDrill?: DrillDescriptor | null
}) {
  const { open } = useDrillDown()
  const maxAmount = Math.max(0, ...data.points.map((point) => point.loanAmount))
  const labelKeys = new Set(data.points.slice(0, 5).map((point) => `${point.city}|${point.state}`))

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 lg:flex-row">
      <div className="relative min-h-[220px] min-w-0 flex-[3] overflow-hidden rounded-md border border-amber-400/10 bg-black/20">
        {data.points.length === 0 ? (
          <div className="flex h-full min-h-[220px] flex-col items-center justify-center px-6 text-center">
            <Unlink size={18} className="mb-2 text-stone-600" aria-hidden="true" />
            <p className="text-[11px] font-medium text-stone-300">No mapped continental-US cities</p>
            <p className="mt-1 max-w-md text-[10px] leading-relaxed text-stone-500">
              A deal appears only after it links to a property with city, state, latitude and
              longitude. No location was inferred from free text.
            </p>
          </div>
        ) : (
          <svg
            viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
            role="img"
            aria-label={`Continental-US city concentration plot with ${data.points.length} mapped cities`}
            className="h-full min-h-[220px] w-full"
          >
            <rect
              x={PLOT.left}
              y={PLOT.top}
              width={VIEW_WIDTH - PLOT.left - PLOT.right}
              height={VIEW_HEIGHT - PLOT.top - PLOT.bottom}
              rx="6"
              fill="rgba(255,255,255,0.015)"
              stroke="rgba(251,191,36,0.10)"
            />

            {/* The ground. Landmass first as one shape so interior borders do
                not double-strike, then state hairlines over it. Both sit under
                the graticule and the markers: this is the reference frame, not
                the subject, and the exposure bubbles must stay the brightest
                thing on the plot. */}
            <g aria-hidden="true">
              <path
                d={GEOGRAPHY.landmass}
                fill="rgba(255,255,255,0.045)"
                stroke="none"
                fillRule="evenodd"
              />
              {GEOGRAPHY.states.map((state) => (
                <path
                  key={state.name}
                  d={state.d}
                  fill="none"
                  stroke="rgba(168,162,158,0.22)"
                  strokeWidth="0.5"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </g>
            {[-120, -110, -100, -90, -80, -70].map((longitude) => {
              const { x } = project({ longitude, latitude: LAT.min } as CityConcentrationPoint)
              return (
                <g key={longitude}>
                  <line
                    x1={x}
                    x2={x}
                    y1={PLOT.top}
                    y2={VIEW_HEIGHT - PLOT.bottom}
                    stroke="rgba(168,162,158,0.10)"
                    strokeDasharray="3 6"
                  />
                  <text x={x} y={VIEW_HEIGHT - 12} textAnchor="middle" className="fill-stone-600 text-[9px]">
                    {Math.abs(longitude)}°W
                  </text>
                </g>
              )
            })}
            {[25, 30, 35, 40, 45, 50].map((latitude) => {
              const { y } = project({ longitude: LNG.min, latitude } as CityConcentrationPoint)
              return (
                <g key={latitude}>
                  <line
                    x1={PLOT.left}
                    x2={VIEW_WIDTH - PLOT.right}
                    y1={y}
                    y2={y}
                    stroke="rgba(168,162,158,0.10)"
                    strokeDasharray="3 6"
                  />
                  <text x={4} y={y + 3} className="fill-stone-600 text-[9px]">
                    {latitude}°N
                  </text>
                </g>
              )
            })}
            {data.points.map((point) => {
              const { x, y } = project(point)
              const radius = maxAmount > 0 ? 5 + Math.sqrt(point.loanAmount / maxAmount) * 13 : 5
              const key = `${point.city}|${point.state}`
              const origin = originText(point, 'deals')
              return (
                <g key={key}>
                  <circle
                    cx={x}
                    cy={y}
                    r={radius}
                    fill="rgba(251,191,36,0.32)"
                    stroke="rgba(253,230,138,0.92)"
                    strokeWidth="1.2"
                  >
                    <title>
                      {point.city}, {point.state}: {money(point.loanAmount)} across {point.dealCount}{' '}
                      {point.dealCount === 1 ? 'deal' : 'deals'} ({percent(point.shareOfBookPct)} of book)
                      {origin && ` · ${origin}`}
                    </title>
                  </circle>
                  {labelKeys.has(key) && (
                    <text
                      x={x + radius + 4}
                      y={y + 3}
                      className="fill-stone-300 text-[9px]"
                      paintOrder="stroke"
                      stroke="rgba(8,8,8,0.92)"
                      strokeWidth="3"
                    >
                      {point.city}, {point.state}
                    </text>
                  )}
                </g>
              )
            })}
          </svg>
        )}

        <div className="pointer-events-none absolute bottom-1.5 right-2 max-w-[80%] text-right font-mono text-[8px] text-stone-600">
          {originLegend(data) ?? 'Property coordinates'} · equirectangular display
        </div>
      </div>

      <div className="min-h-0 min-w-[190px] flex-1 overflow-y-auto scrollbar-thin">
        <div className="mb-2 grid grid-cols-2 gap-1.5">
          <div className="surface-panel rounded-md px-2 py-1.5">
            <div className="terminal-kicker !text-[7px]">Mapped exposure</div>
            <div className="mt-0.5 font-mono text-[11px] text-stone-200">
              {money(data.coverage.mappedAmount)}
            </div>
            <div className="text-[8.5px] text-stone-600">
              {percent(data.coverage.mappedAmountPctOfBook)} of book
            </div>
          </div>
          {(() => {
            const serverIds = data.coverage.unmappedIds
            const drill: DrillDescriptor | null =
              data.unmappedDrill ??
              (serverIds && serverIds.length > 0
                ? { dataset: 'loans', filters: { ids: serverIds }, title: `Unmapped deals (${serverIds.length})` }
                : unmappedDrill ?? null)
            const body = (
              <>
                <div className="terminal-kicker !text-[7px]">Unmapped</div>
                <div className="mt-0.5 font-mono text-[11px] text-stone-200">
                  {data.coverage.unmappedDeals.toLocaleString()} deals
                </div>
                <div className="text-[8.5px] text-stone-600">{money(data.coverage.unmappedAmount)}</div>
              </>
            )
            return drill ? (
              <button
                type="button"
                onClick={() => open(drill)}
                title={`${drill.title} — open records with the reason each is off the map`}
                className="surface-panel rounded-md px-2 py-1.5 text-left transition-colors hover:border-amber-400/35 hover:bg-amber-400/[0.05] focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
              >
                {body}
              </button>
            ) : (
              <div className="surface-panel rounded-md px-2 py-1.5">{body}</div>
            )
          })()}
        </div>

        <ul className="space-y-1" aria-label="City concentrations ranked by loan amount">
          {data.points.map((point, index) => (
            <li key={`${point.city}|${point.state}`}>
              <button
                type="button"
                onClick={() => open(point.drill)}
                disabled={!point.drill}
                className="surface-panel flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-amber-400/[0.06] focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50 disabled:cursor-default disabled:hover:bg-transparent"
                title={point.drill ? `${point.drill.title} — open records` : undefined}
              >
                <span className="w-4 flex-shrink-0 font-mono text-[8px] text-stone-600">{index + 1}</span>
                <MapPin size={10} className="flex-shrink-0 text-amber-300" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-[10px] text-stone-300">
                  {point.city}, {point.state}
                </span>
                <span className="flex-shrink-0 text-right font-mono text-[9.5px] text-stone-200">
                  {money(point.loanAmount)}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {(data.coverage.unmappedReasons.length > 0 || data.coverage.excludedNonContinentalDeals > 0) && (
          <div className="mt-2 rounded-md border border-dashed border-amber-400/15 px-2 py-1.5 text-[9px] leading-relaxed text-stone-500">
            {data.coverage.unmappedReasons.map((reason) => (
              <div key={reason.code}>
                {reason.dealCount} {data.unmappedReasonLabels?.[reason.code] ?? reasonLabel(reason.code)} ·{' '}
                {money(reason.loanAmount)}
              </div>
            ))}
            {(data.unmappedDeals ?? []).length > 0 && (
              <ul className="mt-1 space-y-0.5 border-t border-amber-400/[0.08] pt-1" aria-label="Deals not on the map">
                {(data.unmappedDeals ?? []).map((deal) => (
                  <li key={deal.id}>
                    <button
                      type="button"
                      onClick={() => open({ dataset: 'loans', filters: { ids: [deal.id] }, title: deal.name ?? deal.id })}
                      className="flex w-full items-baseline gap-1 text-left hover:text-amber-200"
                      title={deal.reasonLabel ?? reasonLabel(deal.reason)}
                    >
                      <span className="min-w-0 flex-1 truncate text-stone-400">{deal.name ?? deal.id}</span>
                      <span className="flex-shrink-0 text-stone-600">
                        {deal.reasonLabel ?? reasonLabel(deal.reason)}
                        {deal.fixable ? ' · fixable' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {data.coverage.excludedNonContinentalDeals > 0 && (
              <div>
                {data.coverage.excludedNonContinentalDeals} non-continental US deals excluded ·{' '}
                {money(data.coverage.excludedNonContinentalAmount)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
